<script lang="ts">
  import { untrack } from "svelte";
  import { focus } from "./actions/focus";
  import type { PaletteItem, PaletteLists } from "./palette";
  import { paletteFilter, paletteHints, paletteModes, paletteMove, paletteParse } from "./palette";
  import { len } from "./util";

  interface Props {
    // what is in the box at first: "" or the prefix of a list
    initialQuery?: string;
    lists: PaletteLists;
    onclose: () => void;
    // the palette is closed by the one who runs the row
    onrun: (item: PaletteItem) => void;
    onremove: (item: PaletteItem) => void;
  }

  let { initialQuery = "", lists, onclose, onrun, onremove }: Props = $props();

  let elInput: HTMLInputElement | undefined = $state();
  let elList: HTMLElement | undefined = $state();

  let query = $state(untrack(() => initialQuery));
  let parsed = $derived(paletteParse(query));
  let items = $derived(paletteFilter(lists[parsed.mode], parsed.text));
  // the one selected row, changed by the keys or by moving the mouse
  let selIdx = $state(0);
  // a list that got shorter, as after Del
  let curIdx = $derived(Math.min(selIdx, Math.max(0, len(items) - 1)));
  let hints = $derived(paletteHints(parsed.mode));

  function select(idx: number) {
    selIdx = idx;
    // after the list is drawn
    requestAnimationFrame(() => {
      elList?.querySelector(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" });
    });
  }

  function setMode(prefix: string) {
    query = prefix;
    select(0);
    elInput?.focus();
  }

  function onKeyDown(ev: KeyboardEvent) {
    // the keys are the palette's own while it is open
    ev.stopPropagation();
    if (ev.key == "Escape") {
      ev.preventDefault();
      onclose();
      return;
    }
    if (ev.key == "Tab") {
      // the focus stays in the box
      ev.preventDefault();
      return;
    }
    if (ev.ctrlKey || ev.metaKey || ev.altKey) {
      return;
    }
    if (ev.key == "Enter") {
      ev.preventDefault();
      let item = items[curIdx];
      if (item) {
        onrun(item);
      }
      return;
    }
    if (ev.key == "Delete") {
      // Del at the end of the text has nothing to delete there
      let atEnd = elInput?.selectionStart == len(query) && elInput?.selectionEnd == len(query);
      let item = items[curIdx];
      if (atEnd && item?.remove) {
        ev.preventDefault();
        onremove(item);
      }
      return;
    }
    let idx = paletteMove(curIdx, len(items), ev.key);
    if (idx >= 0) {
      ev.preventDefault();
      select(idx);
    }
  }

  // where the mouse was when it last moved over a row
  let lastMouseX = -1;
  let lastMouseY = -1;

  function onRowMouseMove(ev: MouseEvent, idx: number) {
    // the browser also sends this when the list moves under a still mouse
    if (ev.screenX == lastMouseX && ev.screenY == lastMouseY) {
      return;
    }
    lastMouseX = ev.screenX;
    lastMouseY = ev.screenY;
    selIdx = idx;
  }

  function onBackdropMouseDown(ev: MouseEvent) {
    if (ev.target == ev.currentTarget) {
      ev.preventDefault();
      onclose();
    }
  }

  function onPanelMouseDown(ev: MouseEvent) {
    // a click in the panel does not take the focus from the box
    if (ev.target != elInput) {
      ev.preventDefault();
    }
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="command-palette fixed inset-0 z-50 flex justify-center items-start text-black"
  onmousedown={onBackdropMouseDown}
  onkeydown={onKeyDown}
  oncontextmenu={(ev) => ev.stopPropagation()}
>
  <div class="panel flex flex-col bg-white border border-gray-300 shadow-xl text-sm" onmousedown={onPanelMouseDown}>
    <input
      class="m-2 px-2 py-1 border border-gray-300 outline-none focus:border-sky-400"
      type="text"
      spellcheck="false"
      autocomplete="off"
      placeholder="Type to search"
      aria-label="Command palette"
      bind:this={elInput}
      bind:value={query}
      oninput={() => select(0)}
      use:focus
    />

    <div class="flex flex-row flex-wrap gap-x-3 gap-y-1 px-2 pb-2 text-xs text-gray-600">
      {#each paletteModes as def (def.prefix)}
        <button
          class="flex flex-row items-center gap-1 px-1 cursor-pointer hover:bg-gray-100"
          class:font-bold={def.mode == parsed.mode}
          tabindex="-1"
          onclick={() => setMode(def.prefix)}
        >
          <span class="keycap">{def.prefix}</span>
          {def.name}
        </button>
      {/each}
    </div>

    <div class="list overflow-y-auto border-t border-b border-gray-200" role="listbox" bind:this={elList}>
      {#each items as item, idx}
        <!-- the box has the focus and the keys, not the rows -->
        <!-- svelte-ignore a11y_click_events_have_key_events, a11y_interactive_supports_focus -->
        <div
          role="option"
          aria-selected={idx == curIdx}
          data-idx={idx}
          class="flex flex-row justify-between gap-4 px-3 py-1 cursor-pointer whitespace-nowrap"
          class:bg-sky-200={idx == curIdx}
          onmousemove={(ev) => onRowMouseMove(ev, idx)}
          onclick={() => onrun(item)}
        >
          <div class="truncate">
            {#if item.html}
              {@html item.html}
            {:else}
              {item.label}
            {/if}
          </div>
          {#if item.hint}
            <div class="text-gray-600 shrink-0">{item.hint}</div>
          {/if}
        </div>
      {:else}
        <div class="px-3 py-2 text-gray-500">Nothing to show</div>
      {/each}
    </div>

    <div class="flex flex-row flex-wrap gap-x-4 gap-y-1 px-3 py-1 text-xs text-gray-600">
      {#each hints as hint (hint.key)}
        <div class="flex flex-row items-center gap-1">
          <span class="keycap">{hint.key}</span>
          {hint.desc}
        </div>
      {/each}
    </div>
  </div>
</div>

<style lang="postcss">
  .panel {
    margin-top: 12vh;
    width: min(560px, 92vw);
  }
  .list {
    max-height: min(50vh, 420px);
    min-height: 2rem;
  }
  .keycap {
    display: inline-block;
    min-width: 1.25rem;
    padding: 0 0.25rem;
    text-align: center;
    font-family: var(--font-monospace);
    border: 1px solid #d1d5db;
    border-radius: 3px;
    background: #f9fafb;
    color: #374151;
  }
</style>
